import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { Box, Button, ButtonProps, IconButton } from '@landr/maestro';
import { noop } from 'utils/noop';
import {
    usePrivateActionClickHandler,
    PrivateActionClickHandlerParams,
} from 'hooks/usePrivateActionClickHandler/usePrivateActionClickHandler';
import { AnyAuthenticatedUserAction } from 'types';

export const ActionButtonWrapper = styled(Box)`
    :focus-visible {
        outline: none;
    }
`;

export interface ActionButtonProps
    extends Omit<ButtonProps, 'onClick'>,
        Omit<PrivateActionClickHandlerParams, 'userAction'> {
    label: string;
    userAction?: AnyAuthenticatedUserAction;
    tabIndex?: number;
    children?: React.ReactNode;
}

export const ActionButton = forwardRef(
    (
        {
            authOnly = false,
            onClick = noop,
            preventDefault = true,
            tabIndex,
            stopPropagation = true,
            analyticsCallback = noop,
            isLoading,
            children,
            label,
            userAction = { type: '', description: '', args: [] },
            ...props
        }: ActionButtonProps,
        ref: React.Ref<HTMLDivElement>,
    ) => {
        const handleClick = usePrivateActionClickHandler({
            authOnly,
            onClick,
            preventDefault,
            stopPropagation,
            analyticsCallback,
            isLoading,
            userAction,
        });

        return children ? (
            <ActionButtonWrapper tabIndex={-1} ref={ref}>
                <Button
                    isLoading={isLoading}
                    tabIndex={tabIndex}
                    onClick={handleClick}
                    {...props}
                >
                    {children}
                </Button>
            </ActionButtonWrapper>
        ) : (
            <ActionButtonWrapper tabIndex={-1} ref={ref}>
                <IconButton
                    isLoading={isLoading}
                    tabIndex={tabIndex}
                    onClick={handleClick}
                    label={label}
                    {...props}
                ></IconButton>
            </ActionButtonWrapper>
        );
    },
);
